export function isHeic(file: Blob & { name?: string }): boolean {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    /\.(heic|heif)$/i.test(file.name ?? "")
  );
}

export async function toJpegBlob(file: Blob & { name?: string }): Promise<Blob> {
  if (!isHeic(file)) return file;
  // Lazy-load heic2any so the ~1.4MB decoder doesn't ship in the initial bundle
  const { default: heic2any } = await import("heic2any");
  const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 1 });
  return Array.isArray(result) ? result[0] : result;
}

// Draws the image onto a canvas and re-encodes it as JPEG, which also drops
// all EXIF metadata (camera, time, GPS location).
export async function resizeImage(blob: Blob, maxPx = 1080, quality = 0.8): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't read the image."));
    };
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      // JPEG has no transparency: paint white first so transparent PNG areas
      // don't turn black.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Couldn't encode the image."))),
        "image/jpeg",
        quality
      );
    };
    img.src = url;
  });
}
